import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import {
  X,
  Send,
  Sparkles,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  DollarSign,
  Star,
  ChevronRight,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import toast from 'react-hot-toast';
import { RealTimeActions, ActionItem } from './RealTimeActions';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  action?: {
    type: string;
    success?: boolean;
  };
}

interface TokensInfo {
  available: boolean;
  tokens_limit: number | null;
  tokens_used: number;
  tokens_remaining: number | null;
  display_limit: number | null;
  display_used: number | null;
  display_remaining: number | null;
}

interface QuickAction {
  icon: any;
  title: string;
  subtitle: string;
  query: string;
  gradient: string;
  iconBg: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    icon: Calendar,
    title: 'תורים היום',
    subtitle: 'מי מגיע אליי היום?',
    query: 'מה התורים שלי היום?',
    gradient: 'from-purple-500 to-indigo-500',
    iconBg: 'bg-purple-50',
  },
  {
    icon: Users,
    title: 'לקוחות חדשים',
    subtitle: 'מי הצטרף לאחרונה?',
    query: 'הצג 5 לקוחות אחרונים',
    gradient: 'from-gray-600 to-gray-500',
    iconBg: 'bg-gray-50',
  },
  {
    icon: DollarSign,
    title: 'הכנסות השבוע',
    subtitle: 'כמה הרווחתי?',
    query: 'כמה הרווחתי השבוע?',
    gradient: 'from-purple-500 to-indigo-500',
    iconBg: 'bg-purple-50',
  },
  {
    icon: Clock,
    title: 'ממתינים לאישור',
    subtitle: 'תורים שטרם אושרו',
    query: 'תורים ממתינים לאישור',
    gradient: 'from-gray-600 to-gray-500',
    iconBg: 'bg-gray-50',
  },
  {
    icon: Star,
    title: 'לקוחות VIP',
    subtitle: 'הנאמנים ביותר',
    query: 'מי הלקוחות הנאמנים שלי?',
    gradient: 'from-purple-500 to-indigo-500',
    iconBg: 'bg-purple-50',
  },
  {
    icon: TrendingUp,
    title: 'שירותים פופולריים',
    subtitle: 'מה הכי מבוקש?',
    query: 'מה השירותים הפופולריים?',
    gradient: 'from-gray-600 to-gray-500',
    iconBg: 'bg-gray-50',
  },
];

interface LoLytaAIProps {
  tokensInfo: TokensInfo | null;
  isBlocked?: boolean;
}

export function LoLytaAI({ tokensInfo, isBlocked = false }: LoLytaAIProps) {
  const { user } = useAuth();
  const businessId = user?.user_metadata?.business_id;

  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
  };
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [realtimeActions, setRealtimeActions] = useState<ActionItem[]>([]);
  const [conversationContext, setConversationContext] = useState<any>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isAutoScrolling = useRef(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const scrollToBottom = useCallback((force = false) => {
    if (!messagesEndRef.current || !messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 150;

    if (force || isAutoScrolling.current || isNearBottom) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
      });
    }
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 150;
      isAutoScrolling.current = isNearBottom;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, realtimeActions, scrollToBottom]);

  useEffect(() => {
    const event = new CustomEvent('ui:aichat', { detail: { open: isOpen } });
    window.dispatchEvent(event);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `היי! 👋 אני לוליטה - העוזרת האישית שלך!\n\nאני כאן כדי לנהל את העסק שלך ולעזור לך בכל דבר:\n\n✨ קביעת ותזמון תורים\n👥 ניהול מלא של לקוחות\n📊 דוחות והכנסות בזמן אמת\n📱 שליחת הודעות ותזכורות\n⚡ ועוד המון...\n\nמה תרצה לעשות?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || isBlocked) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowQuickActions(false);

    // Start with thinking action
    const thinkingAction: ActionItem = {
      id: `action-thinking-${Date.now()}`,
      name: 'thinking',
      status: 'in_progress' as const,
      message: 'לוליטה חושבת...',
      timestamp: new Date(),
    };
    setRealtimeActions([thinkingAction]);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      // Simulate routing step after a brief delay
      setTimeout(() => {
        setRealtimeActions(prev => [
          { ...prev[0], status: 'completed' as const, message: 'ניתוח הבקשה' },
          {
            id: `action-routing-${Date.now()}`,
            name: 'routing',
            status: 'in_progress' as const,
            message: 'מנתבת לסוכן המתאים...',
            timestamp: new Date(),
          }
        ]);
      }, 400);

      const { data, error } = await supabase.functions.invoke('ai-orchestrator', {
        body: {
          message: text,
          businessId: businessId,
          userId: user?.id,
          conversationHistory: messages
            .filter(m => m.role !== 'system')
            .map(m => ({
              role: m.role,
              content: m.content
            }))
            .slice(-10),
          context: conversationContext // ✅ CRITICAL: Pass context between messages
        },
      });

      if (error) throw error;

      // Create visual actions for real-time display
      const actions: ActionItem[] = [
        { ...thinkingAction, status: 'completed' as const, message: 'ניתוח הבקשה' }
      ];

      // Add routing action
      if (data.agent) {
        const agentNames: Record<string, string> = {
          'scheduling': 'תזמון תורים',
          'customer': 'ניהול לקוחות',
          'business': 'ניהול עסק',
          'analytics': 'דוחות וסטטיסטיקה',
        };

        actions.push({
          id: `action-routing-${Date.now()}`,
          name: 'routing',
          status: 'completed' as const,
          message: `מנותבת ל${agentNames[data.agent] || data.agent}`,
          timestamp: new Date(),
        });
      }

      // Add tool actions
      if (data.toolsUsed && Array.isArray(data.toolsUsed)) {
        const toolNames: Record<string, string> = {
          'get_appointments': 'מחפש תורים',
          'search_customers': 'מחפש לקוחות',
          'get_staff': 'טוען צוות',
          'get_services': 'טוען שירותים',
          'get_business_hours': 'טוען שעות פעילות',
          'create_appointment': 'יוצר תור חדש',
          'cancel_appointment': 'מבטל תור',
          'cancel_appointments': 'מבטל תורים',
          'update_appointment': 'מעדכן תור',
          'delete_appointment': 'מוחק תור',
          'get_statistics': 'מחשב סטטיסטיקות',
          'create_customer': 'יוצר לקוח חדש',
        };

        data.toolsUsed.forEach((tool: string, idx: number) => {
          actions.push({
            id: `action-tool-${Date.now()}-${idx}`,
            name: tool,
            status: 'completed' as const,
            message: toolNames[tool] || tool,
            timestamp: new Date(),
          });
        });
      }

      // Default action if no specific tools
      if (actions.length === 1) {
        actions.push({
          id: `action-process-${Date.now()}`,
          name: 'process_request',
          status: 'completed' as const,
          message: 'מעבד בקשה',
          timestamp: new Date(),
        });
      }

      setRealtimeActions(actions);

      let content = '';
      if (data.reply) {
        content = data.reply;
      } else if (data.message) {
        content = data.message;
      } else if (data.result) {
        if (typeof data.result === 'string') {
          content = data.result;
        } else {
          content = JSON.stringify(data.result, null, 2);
        }
      } else {
        content = 'הפעולה בוצעה בהצלחה!';
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content,
        timestamp: new Date(),
        action: data.toolsUsed?.length > 0 ? { type: 'query', success: true } : undefined,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // ✅ CRITICAL: Save or clear context
      if (data.context === null) {
        // Flow completed - clear context
        setConversationContext(null);
      } else if (data.context) {
        // Continue flow - save context
        setConversationContext(data.context);
      }
    } catch (error: any) {
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `אופס! משהו השתבש: ${error.message || 'נסי שוב'}`,
        timestamp: new Date(),
        action: { type: 'error', success: false },
      };
      setMessages((prev) => [...prev, errorMessage]);
      toast.error('שגיאה בשליחת הבקשה');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getTokenColor = () => {
    const percentage = tokensInfo?.display_limit
      ? ((tokensInfo.display_remaining || 0) / tokensInfo.display_limit) * 100
      : 100;

    if (percentage > 50) return 'from-green-400 to-emerald-500';
    if (percentage > 20) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-pink-500';
  };

  return (
    <>
      {/* Floating Button - More Elegant */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 left-4 sm:bottom-24 sm:left-6 z-50"
        >
          <div className="relative">
            {/* Glow Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-500 to-blue-500 rounded-full blur-xl opacity-70"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            {/* Main Button */}
            <div className="relative p-4 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full shadow-2xl">
              <Sparkles className="h-7 w-7 text-white" />
            </div>

            {/* Active Indicator */}
            <motion.div
              className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full border-2 border-white shadow-lg"
              animate={{
                scale: [1, 1.3, 1],
                boxShadow: [
                  '0 0 0 0 rgba(16, 185, 129, 0.4)',
                  '0 0 0 8px rgba(16, 185, 129, 0)',
                  '0 0 0 0 rgba(16, 185, 129, 0)',
                ],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
              }}
            />
          </div>

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            whileHover={{ opacity: 1, x: 0 }}
            className="absolute right-full top-1/2 -translate-y-1/2 mr-3 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg shadow-xl whitespace-nowrap pointer-events-none"
          >
            לוליטה AI - העוזרת שלך
            <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-gray-900 rotate-45" />
          </motion.div>
        </motion.button>
      )}

      {/* Chat Window - Premium Design */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - רק בדסקטופ */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="hidden md:block fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.9 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 right-0 bottom-0 md:bottom-20 md:left-4 md:right-auto z-50 md:w-[420px] md:h-[680px] md:top-auto bg-white md:rounded-3xl shadow-2xl flex flex-col overflow-hidden border-0 md:border md:border-gray-200"
              style={{
                top: window.innerWidth < 768 ? 'var(--header-total, 56px)' : undefined,
              }}
            >
            {/* Header - קלאסי ומינימליסטי */}
            <div className="relative bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-3 border-b border-purple-400/20">
              <div className="relative">
                <button
                  onClick={handleClose}
                  className="absolute left-0 top-0 p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 text-white" />
                </button>

                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="h-6 w-6 text-white" />
                  <h2 className="text-xl font-semibold text-white">Lolyta AI</h2>
                  <motion.div
                    className="w-2 h-2 bg-green-400 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
              </div>

              {/* Tokens Info - מינימליסטי */}
              {tokensInfo && (
                <div className="px-4 pb-2 mt-2">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-white/80 font-medium">טוקנים זמינים</span>
                    <span className="font-semibold text-white">
                      {tokensInfo.display_remaining?.toLocaleString() || 0} / {tokensInfo.display_limit?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.max(
                          0,
                          ((tokensInfo.display_remaining || 0) / (tokensInfo.display_limit || 1)) * 100
                        )}%`,
                      }}
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Messages Area */}
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto px-4 py-5 bg-gray-50 scroll-smooth"
              style={{
                overflowAnchor: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {/* Quick Actions Grid */}
              {showQuickActions && messages.length <= 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 mb-3">
                    <p className="text-gray-800 text-[13px] leading-relaxed">
                      שלום! 👋
                      <br />
                      <br />
                      אני לוליטה, העוזרת האישית שלך. אני כאן כדי לעזור לך לנהל את העסק בצורה הכי חכמה ויעילה.
                      <br />
                      <br />
                      בחרי פעולה או שאלי אותי כל דבר! ✨
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {QUICK_ACTIONS.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <motion.button
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.08 }}
                          whileHover={{ scale: 1.03, y: -2 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => handleSendMessage(action.query)}
                          className="group relative bg-white rounded-xl p-3 hover:bg-gray-50 active:bg-gray-100 transition-all border border-gray-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 ${action.iconBg} rounded-lg`}>
                              <Icon className={`h-4 w-4 ${action.gradient.includes('purple') ? 'text-purple-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1 text-right">
                              <p className="text-xs font-semibold text-gray-900">
                                {action.title}
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5">{action.subtitle}</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Chat Messages */}
              <LayoutGroup>
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    layout
                    layoutId={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      layout: { duration: 0.2, ease: 'easeOut' },
                      opacity: { duration: 0.2 },
                      y: { duration: 0.2 }
                    }}
                    className={`mb-4 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                  <div
                    className={`max-w-[85%] rounded-3xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg rounded-br-lg'
                        : 'bg-white text-gray-900 shadow-sm border border-gray-100 rounded-bl-lg'
                    }`}
                  >
                    {message.action && (
                      <div
                        className={`flex items-center gap-2 text-xs mb-2 ${
                          message.role === 'user' ? 'text-white/80' : 'text-gray-500'
                        }`}
                      >
                        {message.action.success ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                        )}
                        <span className="font-medium">
                          {message.action.success ? 'הצלחה' : 'שגיאה'}
                        </span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                    <p
                      className={`text-xs mt-2 ${
                        message.role === 'user' ? 'text-white/60' : 'text-gray-400'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString('he-IL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
              </LayoutGroup>

              {/* Real-Time Actions Display */}
              <RealTimeActions
                actions={realtimeActions}
                isVisible={isLoading || realtimeActions.length > 0}
              />

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100">
              {isBlocked ? (
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-4 text-center border-2 border-red-200">
                  <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm font-bold text-red-700 mb-1">נגמרו הטוקנים</p>
                  <button className="text-xs text-red-600 underline font-medium">שדרגי תוכנית</button>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="שאלי אותי כל דבר..."
                    disabled={isLoading || isBlocked}
                    className="flex-1 resize-none bg-gray-50 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 max-h-24 border border-gray-200"
                    rows={1}
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isLoading || isBlocked}
                    className="p-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    <Send className="h-5 w-5" />
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
